import { useEffect, useState, type CSSProperties } from "react";
import { api } from "../api/client";

/**
 * Живой список «кто уже прицепился к игре» под QR-кодами (16.09, владелец / жалоба Влада:
 * игроки сканировали, а оператор не знал, сработало ли — уведомления «игрок добавился»
 * в Telegram выключены с 10.09). Опрашивает GET /api/games/:id/qr/attached каждые 6 с,
 * пока панель открыта; свежий (до 90 с) отмечен «только что».
 */
type Row = { name: string; via: string; at: string | null };
type Resp = { players: Row[]; count: number; ownerLinked: boolean };
/** 19.09: открыл допуск этой игры, но в составе не оказался (истёк, капитанский уже использован…). */
type Pend = { tg: number; at: string; name: string | null; username: string | null };
type PendResp = { pending: Pend[]; count: number };

const VIA: Record<string, string> = {
  owner: "владелец", qr: "по QR", invited: "по приглашению", link: "по ссылке",
  staff: "привязал сотрудник", manual: "привязал сотрудник", waiver: "по вейверу",
};

const wrap: CSSProperties = {
  marginTop: 12, padding: 12, borderRadius: 14,
  border: "1px solid var(--surface-3, rgba(255,255,255,.18))", background: "rgba(255,255,255,.035)",
};
const chip: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999,
  border: "1px solid var(--surface-3, rgba(255,255,255,.18))", fontSize: 13,
};

function ageSec(at: string | null, now: number): number {
  if (!at) return Infinity;
  const t = Date.parse(at.includes("T") ? at : at.replace(" ", "T") + "Z");
  return Number.isFinite(t) ? Math.max(0, (now - t) / 1000) : Infinity;
}

export function QrAttachedList({ bookingId, now }: { bookingId: number | string; now: number }) {
  const [data, setData] = useState<Resp | null>(null);
  const [failed, setFailed] = useState(false);
  const [pend, setPend] = useState<Pend[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [pErr, setPErr] = useState("");

  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      api<Resp>(`/api/games/${encodeURIComponent(String(bookingId))}/qr/attached`, { retries: 0 })
        .then((r) => { if (alive) { setData(r); setFailed(false); } })
        .catch(() => { if (alive) setFailed(true); });
    };
    tick();
    const id = window.setInterval(tick, 6000);
    return () => { alive = false; window.clearInterval(id); };
  }, [bookingId, reload]);

  /* Список «открывали, но не в игре» тяжелее (скан событий) — опрашиваем реже. */
  useEffect(() => {
    let alive = true;
    const tick = () => {
      api<PendResp>(`/api/games/${encodeURIComponent(String(bookingId))}/qr/pending`, { retries: 0 })
        .then((r) => { if (alive) setPend(r.pending ?? []); })
        .catch(() => { /* список подсказок не критичен */ });
    };
    tick();
    const id = window.setInterval(tick, 30000);
    return () => { alive = false; window.clearInterval(id); };
  }, [bookingId, reload]);

  const attach = async (p: Pend) => {
    setBusy(p.tg); setPErr("");
    try {
      await api(`/api/games/${encodeURIComponent(String(bookingId))}/qr/attach`, { method: "POST", body: { telegram_id: p.tg }, retries: 0 });
      setPend((list) => list.filter((x) => x.tg !== p.tg));
      setReload((n) => n + 1);
    } catch (e: unknown) {
      setPErr(e instanceof Error ? e.message : "Не удалось прицепить");
    } finally { setBusy(null); }
  };

  return (
    <section style={wrap} aria-live="polite">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, textTransform: "uppercase", letterSpacing: ".08em" }}>
          В игре из Юнифанса{data ? `: ${data.count}` : ""}
        </h3>
        <span style={{ fontSize: 11, color: "var(--text-3, #8e8a82)" }}>обновляется само</span>
      </div>
      {failed && !data ? <p style={{ margin: 0, color: "var(--red, #ff4d5e)", fontSize: 12 }}>Список не загрузился — проверь сеть.</p> : null}
      {data && data.count === 0 ? (
        <p style={{ margin: 0, color: "var(--text-2, #c8c2b7)", fontSize: 13 }}>
          Пока никого. Игрок сканирует QR камерой Telegram или из приложения (кнопка «Допуск») —
          как только откроет ссылку, появится здесь. Если не появился — скан не дошёл, пусть сканирует ещё раз.
        </p>
      ) : null}
      {data && data.count > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {data.players.map((p, i) => {
            const fresh = ageSec(p.at, now) < 90;
            return (
              <span key={`${p.name}-${i}`} style={{ ...chip, borderColor: fresh ? "var(--green, #00ff9d)" : undefined }}>
                <b>{p.name}</b>
                <i style={{ fontStyle: "normal", color: "var(--text-3, #8e8a82)", fontSize: 11 }}>{VIA[p.via] ?? p.via}</i>
                {fresh ? <i style={{ fontStyle: "normal", color: "var(--green, #00ff9d)", fontSize: 11 }}>только что</i> : null}
              </span>
            );
          })}
        </div>
      ) : null}
      {/* 19.09 (владелец: «он переходит, а сессии у него нет, и я не могу отследить ни его номер, ни тг») */}
      {pend.length > 0 ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--surface-3, rgba(255,255,255,.18))" }}>
          <div style={{ fontSize: 12, color: "var(--orange, #ffa033)", marginBottom: 6 }}>
            Открывали допуск, но в игру не попали — допуск истёк или капитанский уже использован. Тап по «прицепить» добавит игру.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {pend.map((p) => (
              <span key={p.tg} style={{ ...chip, borderColor: "var(--orange, #ffa033)" }}>
                <b>{p.name || `без имени · id ${p.tg}`}</b>
                <i style={{ fontStyle: "normal", color: "var(--text-3, #8e8a82)", fontSize: 11 }}>{fmtAt(p.at)}</i>
                <button type="button" onClick={() => void attach(p)} disabled={busy === p.tg}
                  style={{ border: 0, background: "var(--orange, #ffa033)", color: "#1a1613", borderRadius: 999, padding: "3px 9px", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
                  {busy === p.tg ? "…" : "прицепить"}
                </button>
              </span>
            ))}
          </div>
          {pErr ? <div style={{ marginTop: 6, color: "var(--red, #ff4d5e)", fontSize: 12 }}>{pErr}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

/** «19:47» / «вчера 19:47» — когда человек открывал допуск. */
function fmtAt(at: string): string {
  const t = Date.parse(at.includes("T") ? at : at.replace(" ", "T") + "Z");
  if (!Number.isFinite(t)) return "";
  const d = new Date(t + 5 * 3600000);   // Оренбург
  const hm = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  const today = new Date(Date.now() + 5 * 3600000).toISOString().slice(0, 10);
  const day = d.toISOString().slice(0, 10);
  return day === today ? hm : `${day.slice(8, 10)}.${day.slice(5, 7)} ${hm}`;
}
